import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FolderTree, Plus, Eye, X, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { categoryApi } from '../lib/api';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function Categories() {
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryApi.list,
  });

  const categories = data?.data?.categories || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600">Organize your documents by category</p>
        </div>
        <button className="btn-primary">
          <Plus className="h-5 w-5" />
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category: any) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category)}
            className="card hover:shadow-md transition-shadow text-left w-full cursor-pointer hover:bg-gray-50"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FolderTree
                    className="h-5 w-5"
                    style={{ color: category.color || '#3B82F6' }}
                  />
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                </div>
                {category.description && (
                  <p className="text-sm text-gray-600 mb-3">
                    {category.description}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  {category.document_count} document
                  {category.document_count !== 1 ? 's' : ''}
                </p>
              </div>
              <Eye className="h-5 w-5 text-gray-400" />
            </div>
          </button>
        ))}
      </div>

      {/* Category Documents Modal */}
      {selectedCategory && (
        <CategoryDocumentsModal
          category={selectedCategory}
          onClose={() => setSelectedCategory(null)}
        />
      )}
    </div>
  );
}

interface CategoryDocumentsModalProps {
  category: any;
  onClose: () => void;
}

function CategoryDocumentsModal({ category, onClose }: CategoryDocumentsModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['categoryDocuments', category.id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/categories/${category.id}/documents`);
      return response.data;
    },
  });

  const documents = data?.documents || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block w-full max-w-4xl overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <FolderTree
                className="h-6 w-6"
                style={{ color: category.color || '#3B82F6' }}
              />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
                <p className="text-sm text-gray-600">
                  {documents.length} document{documents.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No documents in this category yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc: any) => (
                  <Link
                    key={doc.id}
                    to={`/documents/${doc.id}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded {new Date(doc.upload_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.confidence && (
                        <span className="text-xs text-gray-500">
                          {Math.round(doc.confidence * 100)}% match
                        </span>
                      )}
                      <Eye className="h-4 w-4 text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t flex justify-end">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

